/**
 * @file The two lines an attachment transfer row shows, as pure text.
 * Consumer: `AttachmentTransferRow`. Kept out of the component so the copy and
 * its rounding can be pinned by a test that renders nothing.
 *
 * Sending reports a percentage; assembling deliberately does not. A chunked
 * assembly holds bytes in memory rather than moving them anywhere, and a
 * percentage of an assembly reads as a percentage of a download that has not
 * started. The percentage rounds to the nearest whole number and is clamped to
 * 0-100, so a total the writer never learned (`totalBytes` 0) reads as 0%.
 *
 * A failure replaces both lines and never promises a second attempt: a browser
 * upload has no agent behind it, so the row says the transfer did not finish
 * and leaves going again to the Retry beside it.
 */
import { formatBytes } from './formatBytes';
import type { AttachmentTransfer } from '../../../store/attachmentTransfersStore';

/** What a transfer row prints, in reading order above its controls. */
export type AttachmentTransferCaption = {
    /** The first line: what is happening, with a percentage when there is one. */
    headline: string;
    /** The second line: the byte figures behind the headline, or the reason it stopped. */
    detail: string;
};

export const resolveAttachmentTransferCaption = (
    transfer: Pick<AttachmentTransfer, 'kind' | 'bytesTransferred' | 'totalBytes' | 'failure'>,
): AttachmentTransferCaption => {
    const { kind, bytesTransferred, totalBytes, failure } = transfer;

    if (failure) {
        return {
            headline: kind === 'assemble' ? 'Download did not finish' : 'Upload did not finish',
            detail: failure,
        };
    }

    if (kind === 'assemble') {
        return {
            headline: 'Assembling to download',
            detail: `${formatBytes(bytesTransferred)} in memory`,
        };
    }

    const percent = totalBytes > 0
        ? Math.min(100, Math.max(0, Math.round((bytesTransferred / totalBytes) * 100)))
        : 0;

    return {
        headline: `Sending — ${percent}%`,
        detail: `${formatBytes(bytesTransferred)} of ${formatBytes(totalBytes)}`,
    };
};
